import { AssociationWithMembers } from "@boark/shared/types/association-with-members";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { getOwnedAssociations } from "../../services/auth";
import { useAuthStore } from "../../stores/auth";
import { useControlAssociationStore } from "../../stores/control-association";
import { useLocale } from "../../stores/locale";
import { recordIdToId } from "../../utils/record-id-to-id";

export function ControlStart() {
  const { locale, currentLanguage } = useLocale();
  const navigate = useNavigate();
  const { isAuth } = useAuthStore();
  const setSelectedAssociationId = useControlAssociationStore(
    (s) => s.setSelectedAssociationId,
  );

  const [associations, setAssociations] = useState<AssociationWithMembers[]>(
    [],
  );
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!isAuth) {
      return;
    }

    (async () => {
      try {
        const owned = await getOwnedAssociations(currentLanguage);
        setAssociations(owned);
        setLoadError(false);
      } catch {
        setLoadError(true);
      }
    })();
  }, [isAuth, currentLanguage]);

  if (!isAuth) {
    return <Navigate replace to="/login" />;
  }

  function selectAndGo(associationId: string) {
    setSelectedAssociationId(associationId);
    navigate("/admin/users");
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
      <div className="flex flex-col gap-2">
        <H1>{locale.auth.controlStartTitle}</H1>
        <p className="text-gray-600">{locale.auth.controlStartIntro}</p>
      </div>

      {loadError && (
        <p className="text-red-500">{locale.auth.controlStartLoadError}</p>
      )}

      {!loadError && associations.length > 0 && (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-gray-700">
            {locale.auth.controlStartOwnedHeading}
          </h2>
          <ul className="flex flex-col gap-2">
            {associations.map((a) => (
              <li key={a.id}>
                <div className="flex items-stretch gap-2">
                  <button
                    className="min-w-0 flex-1 rounded border border-gray-200 bg-white px-4 py-3 text-left hover:border-blue-400 hover:bg-blue-50"
                    type="button"
                    onClick={() => selectAndGo(a.id)}
                  >
                    <span className="font-medium">{a.name}</span>
                    <span className="block text-sm text-gray-500">
                      {a.domain}
                    </span>
                  </button>
                  <Button
                    route={`/association/${recordIdToId(a.id)}/edit`}
                    severity="secondary"
                  >
                    {locale.auth.associationEditLink}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className={`flex flex-col gap-3 pt-6 ${associations.length > 0 ? "border-t border-gray-200" : ""}`}
      >
        <h2 className="text-sm font-medium text-gray-700">
          {locale.auth.controlStartCreateHeading}
        </h2>
        <Button route="/association/create">
          {locale.auth.controlStartCreateSubmit}
        </Button>
      </div>
    </div>
  );
}
